import React from 'react'
import styled, {css} from 'styled-components'

/**
 * HorizontalStepper
 *
 * Standalone horizontal stepper. Renders a sequence of circular icon
 * steps connected by line segments. Steps have three visual states:
 *
 *   - **done** — completed step (filled cyan)
 *   - **current** — active step (filled cyan with cyan-100 halo)
 *   - **todo** — upcoming step (white with grey border)
 *
 * Used in two contexts:
 *
 *   1. Standalone — onboarding flows, settings wizards, multi-step forms
 *   2. Embedded in `ServiceOrderCard` — the 4-step order pipeline
 *
 * The component is dumb. Steps are passed in as an array, currentStep
 * is 1-indexed, and the parent owns navigation logic.
 *
 * @example basic
 *   <HorizontalStepper
 *     steps={[
 *       { icon: <i className="ti ti-puzzle" />,     label: 'Submit' },
 *       { icon: <i className="ti ti-clipboard" />,  label: 'Process' },
 *       { icon: <i className="ti ti-send" />,       label: 'Confirm' },
 *       { icon: <i className="ti ti-list-check" />, label: 'Fill' },
 *     ]}
 *     currentStep={3}
 *   />
 *
 * @example with labels rendered below each step
 *   <HorizontalStepper steps={steps} currentStep={2} showLabels />
 *
 * @example clickable for navigation
 *   <HorizontalStepper
 *     steps={steps}
 *     currentStep={current}
 *     onStepClick={(stepNum) => setCurrent(stepNum)}
 *   />
 */

export type StepperSize = 'sm' | 'md' | 'lg'

export interface StepperStep {
  /** Tabler icon node or any ReactNode (rendered inside the circle) */
  icon: React.ReactNode
  /** Optional label — shown below the step if `showLabels` is true */
  label?: string
  /** Accessible description of the step (announced to screen readers) */
  ariaLabel?: string
}

export interface HorizontalStepperProps {
  /** Ordered list of steps */
  steps: StepperStep[]
  /** 1-indexed current step. Values < 1 mean nothing done; > steps.length means all done. */
  currentStep: number
  /** Step circle size: sm 24px, md 34px (default), lg 44px */
  size?: StepperSize
  /** Render the step labels below the icons */
  showLabels?: boolean
  /**
   * If provided, makes step circles clickable. Useful for jumping
   * between steps in a wizard. Receives the 1-indexed step number.
   */
  onStepClick?: (stepNumber: number) => void
  /** Optional className for layout (DS-level positioning only) */
  className?: string
  /** Accessible label for the entire progressbar */
  ariaLabel?: string
}

/* ====================================================================
   SIZE TOKENS
   ==================================================================== */
const SIZE_TOKENS: Record<
  StepperSize,
  {
    circle: number
    iconFont: string
    haloRing: number
    lineMinWidth: number
    labelFont: string
  }
> = {
  sm: {
    circle: 24,
    iconFont: '11px',
    haloRing: 3,
    lineMinWidth: 4,
    labelFont: '0.6875rem',
  },
  md: {
    circle: 34,
    iconFont: '15px',
    haloRing: 4,
    lineMinWidth: 6,
    labelFont: '0.75rem',
  },
  lg: {
    circle: 44,
    iconFont: '18px',
    haloRing: 5,
    lineMinWidth: 8,
    labelFont: '0.8125rem',
  },
}

/* ====================================================================
   STYLED
   ==================================================================== */
const Wrap = styled.div<{$showLabels: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$showLabels}) => ($showLabels ? 'var(--space-3)' : '0')};
  width: 100%;
`

const Track = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-1) 0;
`

const StepWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`

const stepStateStyles = {
  done: css`
    background: var(--brand);
    color: var(--text-on-brand);
  `,
  current: (haloRing: number) => css`
    background: var(--brand);
    color: var(--text-on-brand);
    box-shadow: 0 0 0 ${haloRing}px var(--brand-100), 0 2px 6px rgba(3, 169, 252, 0.35);
  `,
  todo: css`
    background: var(--bg-card);
    color: var(--text-disabled);
    border: 1.5px solid var(--neutral-300);
  `,
}

const StepCircle = styled.button<{
  $state: 'done' | 'current' | 'todo'
  $size: StepperSize
  $clickable: boolean
}>`
  width: ${({$size}) => SIZE_TOKENS[$size].circle}px;
  height: ${({$size}) => SIZE_TOKENS[$size].circle}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({$size}) => SIZE_TOKENS[$size].iconFont};
  line-height: 0;
  flex-shrink: 0;
  position: relative;
  transition: background 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
  padding: 0;
  border: 0;
  cursor: ${({$clickable}) => ($clickable ? 'pointer' : 'default')};

  ${({$state, $size}) => {
    if ($state === 'done') return stepStateStyles.done
    if ($state === 'current') return stepStateStyles.current(SIZE_TOKENS[$size].haloRing)
    return stepStateStyles.todo
  }}

  &:focus-visible {
    outline: none;
    box-shadow: ${({$state, $size}) =>
        $state === 'current'
          ? `0 0 0 ${SIZE_TOKENS[$size].haloRing}px var(--brand-100), 0 2px 6px rgba(3, 169, 252, 0.35),`
          : ''}
      var(--focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const StepLine = styled.div<{$done: boolean; $minWidth: number}>`
  flex: 1;
  height: 2px;
  background: ${({$done}) => ($done ? 'var(--brand)' : 'var(--divider)')};
  min-width: ${({$minWidth}) => $minWidth}px;
  transition: background 0.2s;
`

const LabelsRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`

const LabelCell = styled.div<{$isCurrent: boolean; $size: StepperSize}>`
  flex-shrink: 0;
  font-family: var(--font-sans);
  font-size: ${({$size}) => SIZE_TOKENS[$size].labelFont};
  font-weight: ${({$isCurrent}) => ($isCurrent ? 600 : 500)};
  color: ${({$isCurrent}) => ($isCurrent ? 'var(--primary-800)' : 'var(--neutral-700)')};
  text-align: center;
  letter-spacing: -0.005em;
  line-height: 1.3;
  /* Match step circle width so labels sit centered under their step */
  width: ${({$size}) => SIZE_TOKENS[$size].circle}px;
`

const LabelSpacer = styled.div`
  flex: 1;
`

/* ====================================================================
   COMPONENT
   ==================================================================== */
export const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  currentStep,
  size = 'md',
  showLabels = false,
  onStepClick,
  className,
  ariaLabel,
}) => {
  const total = steps.length
  const tokens = SIZE_TOKENS[size]
  const clickable = Boolean(onStepClick)

  const getState = (stepNumber: number): 'done' | 'current' | 'todo' => {
    if (stepNumber < currentStep) return 'done'
    if (stepNumber === currentStep) return 'current'
    return 'todo'
  }

  const handleStepClick = (stepNumber: number) => {
    if (onStepClick) onStepClick(stepNumber)
  }

  // Cap aria-valuenow within the [1, total] range for screen readers
  const ariaValueNow = Math.max(1, Math.min(currentStep, total))

  return (
    <Wrap
      $showLabels={showLabels}
      className={className}
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={ariaValueNow}
      aria-label={ariaLabel ?? `Step ${ariaValueNow} of ${total}`}>
      <Track>
        {steps.map((step, i) => {
          const stepNum = i + 1
          const state = getState(stepNum)
          const isLast = i === steps.length - 1
          const lineDone = stepNum < currentStep

          return (
            <React.Fragment key={i}>
              <StepWrap>
                <StepCircle
                  type="button"
                  $state={state}
                  $size={size}
                  $clickable={clickable}
                  disabled={!clickable}
                  onClick={clickable ? () => handleStepClick(stepNum) : undefined}
                  aria-label={step.ariaLabel ?? step.label ?? `Step ${stepNum}`}
                  aria-current={state === 'current' ? 'step' : undefined}>
                  {step.icon}
                </StepCircle>
              </StepWrap>

              {!isLast && (
                <StepLine $done={lineDone} $minWidth={tokens.lineMinWidth} aria-hidden="true" />
              )}
            </React.Fragment>
          )
        })}
      </Track>

      {showLabels && (
        <LabelsRow aria-hidden="true">
          {steps.map((step, i) => {
            const stepNum = i + 1
            const isCurrent = stepNum === currentStep
            const isLast = i === steps.length - 1
            return (
              <React.Fragment key={`label-${i}`}>
                <LabelCell $isCurrent={isCurrent} $size={size}>
                  {step.label ?? ''}
                </LabelCell>
                {!isLast && <LabelSpacer />}
              </React.Fragment>
            )
          })}
        </LabelsRow>
      )}
    </Wrap>
  )
}

HorizontalStepper.displayName = 'HorizontalStepper'
